<template>
  <div>
    <ValidationObserver ref="observer">
      <div class="vx-row">
        <div class="vx-col w-full">
          <ul class="heading-ul mb-3">
            <li>
              <h3>{{ $t("accounting.label.addjournalentries") }}</h3>
            </li>
          </ul>
        </div>
        <vx-card title no-shadow card-border>
          <div class="vx-row">
            <div class="vx-col w-full lg:w-1/2 md:w-1/2 sm:w-full xs:w-full">
              <div class="dropdown-with-label">
                <small class="date-label font-semibold"
                  >{{ $t("accounting.label.entity")
                  }}<span class="required"> *</span></small
                >
                <ValidationProvider
                  rules="required"
                  v-slot="{ errors }"
                  :name="$t('accounting.label.entity')"
                >
                  <v-select
                    v-if="OfficesList"
                    label="name"
                    v-model="entity"
                    :options="OfficesList"
                    :placeholder="$t('placeholder.label.SelectOne')"
                    :reduce="(obj) => obj.id"
                    :dir="$vs.rtl ? 'rtl' : 'ltr'"
                    class="w-full mb-2"
                  >
                  </v-select>
                  <span class="validate-error" style="color: red">{{
                    errors[0]
                  }}</span>
                </ValidationProvider>
              </div>
            </div>
            <div class="vx-col w-full lg:w-1/2 md:w-1/2 sm:w-full xs:w-full">
              <div class="dropdown-with-label">
                <small class="date-label font-semibold"
                  >{{ $t("accounting.label.currency")
                  }}<span class="required"> *</span></small
                >
                <ValidationProvider
                  rules="required"
                  v-slot="{ errors }"
                  :name="$t('accounting.label.currency')"
                >
                  <v-select
                    label="name"
                    v-model="currency"
                    :options="CurrencyData.selectedCurrencyOptions"
                    :placeholder="$t('placeholder.label.SelectOne')"
                    :reduce="(obj) => obj.code"
                    :dir="$vs.rtl ? 'rtl' : 'ltr'"
                    class="w-full mb-2"
                  >
                  </v-select>
                  <span class="validate-error" style="color: red">{{
                    errors[0]
                  }}</span>
                </ValidationProvider>
              </div>
            </div>
            <div class="w-full ml-5 mt-2">
              <ul class="heading-ul mb-3">
                <li>
                  <h5>{{ $t("accounting.label.affectedglentries") }}</h5>
                </li>
              </ul>
            </div>
            <div class="vx-col w-full md:w-full">
              <div
                class="vx-row"
                v-for="(debitField, index) in debitFields"
                :key="index"
              >
                <div class="custom-width ml-4 dropdown-with-label">
                  <small class="date-label font-semibold">
                    {{ $t("accounting.label.debit")
                    }}<span class="required"> *</span>
                  </small>
                  <v-select
                    v-if="AllowedAccounts"
                    label="name"
                    v-model="debitFields[index].glAccountId"
                    :options="AllowedAccounts"
                    :placeholder="$t('placeholder.label.SelectOne')"
                    :reduce="(obj) => obj.id"
                    :dir="$vs.rtl ? 'rtl' : 'ltr'"
                    class="w-full mb-2"
                  ></v-select>
                </div>
                <div class="md:w-1/3">
                  <vs-input
                    :label="$t('accounting.label.amount')"
                    class="w-full custom-margin mb-2 font-semibold"
                    v-model="debitFields[index].amount"
                  />
                </div>
                <div class="md:w-auto">
                  <feather-icon
                    v-if="index > 0"
                    icon="XCircleIcon"
                    title="Remove"
                    svgClasses="w-7 h-7 stroke-current cursor text-primary"
                    @click.stop="removeDebitField(index)"
                    class="icon_row icon-add"
                  />
                </div>
                <div class="md:w-auto">
                  <feather-icon
                    v-if="index == 0"
                    icon="PlusCircleIcon"
                    title="Add"
                    svgClasses="w-7 h-7 stroke-current cursor text-primary"
                    @click.stop="addDebitField(index)"
                    class="icon_row icon-add"
                  />
                </div>
              </div>
            </div>
            <div class="vx-col w-full md:w-full">
              <div
                class="vx-row"
                v-for="(creditField, index) in creditFields"
                :key="index"
              >
                <div class="custom-width ml-4 dropdown-with-label">
                  <small class="date-label font-semibold">
                    {{ $t("accounting.label.credit")
                    }}<span class="required"> *</span>
                  </small>
                  <v-select
                    label="name"
                    v-if="AllowedAccounts"
                    v-model="creditFields[index].glAccountId"
                    :options="AllowedAccounts"
                    :placeholder="$t('placeholder.label.SelectOne')"
                    :reduce="(obj) => obj.id"
                    :dir="$vs.rtl ? 'rtl' : 'ltr'"
                    class="w-full mb-2"
                  >
                  </v-select>
                </div>
                <div class="md:w-1/3">
                  <vs-input
                    :label="$t('accounting.label.amount')"
                    class="w-full custom-margin mb-2 font-semibold"
                    v-model="creditFields[index].amount"
                  />
                </div>
                <div class="md:w-auto">
                  <feather-icon
                    v-if="index > 0"
                    icon="XCircleIcon"
                    title="Remove"
                    svgClasses="w-7 h-7 stroke-current cursor text-primary"
                    @click.stop="removeCreditField(index)"
                    class="icon_row icon-add"
                  />
                </div>
                <div class="md:w-auto">
                  <feather-icon
                    v-if="index == 0"
                    icon="PlusCircleIcon"
                    title="Add"
                    svgClasses="w-7 h-7 stroke-current cursor text-primary"
                    @click.stop="addCreditField(index)"
                    class="icon_row icon-add"
                  />
                </div>
              </div>
            </div>
            <div class="vx-col w-full lg:w-1/2 md:w-1/2 sm:w-full xs:w-full">
              <vs-input
                :label="$t('accounting.label.referencenumber')"
                class="w-full mb-2 font-semibold"
                v-model="referenceNumber"
              />
            </div>
            <div class="vx-col w-full lg:w-1/2 md:w-1/2 sm:w-full xs:w-full">
              <div class="dropdown-with-label">
                <small class="date-label"
                  >{{ $t("accounting.label.transactiondate")
                  }}<span class="required"> *</span></small
                >
                <ValidationProvider
                  rules="required"
                  v-slot="{ errors }"
                  :name="$t('accounting.label.transactiondate')"
                >
                  <datepicker
                    :placeholder="$t('placeholder.label.SelectOne')"
                    v-model="transactionDate"
                    :dir="$vs.rtl ? 'rtl' : 'ltr'"
                    class="min-w-200 w-full"
                    :disabled-dates="{ from: new Date() }"
                  />
                  <span class="validate-error" style="color: red">{{
                    errors[0]
                  }}</span>
                </ValidationProvider>
              </div>
            </div>
            <div class="vx-col w-full lg:w-1/2 md:w-1/2 sm:w-full xs:w-full">
              <vs-checkbox
                class="mt-5 mb-5 font-semibold"
                v-model="showPaymentDetails"
                >{{ $t("accounting.label.showpaymentdetails") }}</vs-checkbox
              >
            </div>
            <div
              class="vx-col w-full lg:w-1/2 md:w-1/2 sm:w-full xs:w-full"
            ></div>
            <div
              v-if="showPaymentDetails == true"
              class="vx-col w-full lg:w-1/2 md:w-1/2 sm:w-full xs:w-full"
            >
              <div class="dropdown-with-label">
                <small class="date-label font-semibold"
                  >{{ $t("accounting.label.paymenttype")
                  }}<span class="required"> *</span></small
                >
                <v-select
                  label="name"
                  v-model="paymentType"
                  :options="PaymentTypesData"
                  :placeholder="$t('placeholder.label.SelectOne')"
                  :reduce="(obj) => obj.id"
                  :dir="$vs.rtl ? 'rtl' : 'ltr'"
                  class="w-full mb-2"
                >
                </v-select>
              </div>
            </div>
            <div
              v-if="showPaymentDetails == true"
              class="vx-col w-full lg:w-1/2 md:w-1/2 sm:w-full xs:w-full"
            >
              <vs-input
                :label="$t('accounting.label.account')"
                class="w-full mb-2 font-semibold"
                v-model="account"
              />
            </div>
            <div
              v-if="showPaymentDetails == true"
              class="vx-col w-full lg:w-1/2 md:w-1/2 sm:w-full xs:w-full"
            >
              <vs-input
                :label="$t('accounting.label.cheque')"
                class="w-full mb-2 font-semibold"
                v-model="cheque"
              />
            </div>
            <div
              v-if="showPaymentDetails == true"
              class="vx-col w-full lg:w-1/2 md:w-1/2 sm:w-full xs:w-full"
            >
              <vs-input
                :label="$t('accounting.label.routingcode')"
                class="w-full mb-2 font-semibold"
                v-model="routingCode"
              />
            </div>
            <div
              v-if="showPaymentDetails == true"
              class="vx-col w-full lg:w-1/2 md:w-1/2 sm:w-full xs:w-full"
            >
              <vs-input
                :label="$t('accounting.label.receipt')"
                class="w-full mb-2 font-semibold"
                v-model="receipt"
              />
            </div>
            <div
              v-if="showPaymentDetails == true"
              class="vx-col w-full lg:w-1/2 md:w-1/2 sm:w-full xs:w-full"
            >
              <vs-input
                :label="$t('accounting.label.bank')"
                class="w-full mb-2 font-semibold"
                v-model="bank"
              />
            </div>
            <div class="vx-col w-full lg:w-1/2 md:w-1/2 sm:w-full xs:w-full">
              <vs-input
                :label="$t('accounting.label.comment')"
                class="w-full mb-2 font-semibold"
                v-model="comments"
              />
            </div>
          </div>
        </vx-card>
      </div>
      <div class="vx-row center">
        <div class="vx-col w-full">
          <vs-button
            color="primary"
            type="border"
            class="mb-2 footerButton-cancel"
            @click="resetForm"
            >{{ $t("leads.label.reset") }}</vs-button
          >
          <vs-button
            class="mr-3 mb-2 footerButton-next"
            @click="handleSubmit"
            >{{ $t("system.label.Submit") }}</vs-button
          >
        </div>
      </div>
    </ValidationObserver>
    <validation-popup
      :errorMessage="errorMessage"
      :warningPopup="warningPopup"
      :warningText="warningText"
      @closeWarning="warningPopup = false"
    ></validation-popup>
  </div>
</template>
<script>
import vSelect from "vue-select";
import { mapState } from "vuex";
import Datepicker from "vuejs-datepicker";
import moment from "moment";
import { ValidationProvider, ValidationObserver } from "vee-validate";

export default {
  data() {
    return {
      warningPopup: false,
      warningText: "",
      errorMessage: "",
      showPaymentDetails: false,
      transactionDate: moment().format("DD MMMM yyyy"),
      entity: "",
      comments: "",
      currency: "",
      referenceNumber: "",
      paymentType: "",
      account: "",
      cheque: "",
      routingCode: "",
      receipt: "",
      bank: "",
      debitFields: [{ glAccountId: null, amount: null }],
      creditFields: [{ glAccountId: null, amount: null }],
      selectedAccountingRule: null,
    };
  },
  components: {
    "v-select": vSelect,
    ValidationProvider,
    ValidationObserver,
    Datepicker,
  },

  computed: {
    ...mapState({
      OfficesList: (state) => state.accounting.officesList,
      CurrencyData: (state) => state.accounting.currencyData,
      PaymentTypesData: (state) => state.accounting.paymentTypes,
      TransactionId: (state) => state.accounting.reverseTransactionId,
      AllowedAccounts: (state) => state.accounting.allowedAccounts,
    }),
  },
  methods: {
    addDebitField() {
      this.debitFields.push({ glAccountId: null, amount: null });
    },
    removeDebitField(index) {
      this.debitFields.splice(index, 1);
    },
    addCreditField() {
      this.creditFields.push({ glAccountId: null, amount: null });
    },
    removeCreditField(index) {
      this.creditFields.splice(index, 1);
    },
    async getOfficeList() {
      try {
        this.$store.dispatch("accounting/getOfficesListAction");
      } catch (error) {
        throw Error(
          error.message.errors[0].defaultUserMessage ||
            `Api failed with status code: ${error.message.httpStatusCode}`
        );
      }
    },
    async getAllowedAccountsMethod() {
      try {
        await this.$store.dispatch("accounting/getAllowedAccountsAction");
      } catch (error) {
        throw Error(
          error.message.errors[0].defaultUserMessage ||
            `Api failed with status code: ${error.message.httpStatusCode}`
        );
      }
    },
    async getCurrencyDataMethod() {
      try {
        this.$store.dispatch("accounting/getCurrencyDataAction");
      } catch (error) {
        throw Error(
          error.message.errors[0].defaultUserMessage ||
            `Api failed with status code: ${error.message.httpStatusCode}`
        );
      }
    },
    async getPaymentTypesMethod() {
      try {
        this.$store.dispatch("accounting/getPaymentTypesAction");
      } catch (error) {
        throw Error(
          error.message.errors[0].defaultUserMessage ||
            `Api failed with status code: ${error.message.httpStatusCode}`
        );
      }
    },
    async handleSubmit() {
      const isValid = await this.$refs.observer.validate();
      if (!isValid) return;
      const payload = {
        locale: "en",
        dateFormat: "dd MMMM yyyy",
        officeId: this.entity,
        transactionDate: this.transactionDate,
        referenceNumber: this.referenceNumber,
        comments: this.comments,
        currencyCode: this.currency,
        credits: this.creditFields,
        debits: this.debitFields,
      };
      if (this.showPaymentDetails == true) {
        payload.paymentType = this.paymentType;
        payload.accountNumber = this.account;
        payload.bankNumber = this.bank;
        payload.checkNumber = this.cheque;
        payload.receiptNumber = this.receipt;
        payload.routingCode = this.routingCode;
      }
      try {
        await this.$store.dispatch(
          "accounting/addFrequentPostingsAction",
          payload
        );
        await this.$swal({
          title: "Success",
          icon: "success",
          text: "Journal Entry Added Successfully!",
          type: "success",
        });
        this.resetForm();
        this.$router.push({
          name: "View Transaction",
          params: { id: this.TransactionId.transactionId },
        });
      } catch (error) {
        const errorMessage = String(error.httpStatusCode).replace(/^Error:\s*/, "");
        this.$swal({
          title: "Error",
          icon: "error",
          html: `<div style="text-align: center;">${errorMessage}</div>`,
          type: "error",
        });
      }
    },
    resetForm() {
      this.entity = "";
      this.transactionDate = moment().format("DD MMMM yyyy");
      this.referenceNumber = "";
      this.comments = "";
      this.accountingRules = "";
      this.currency = "";
      this.creditFields = [{ glAccountId: null, amount: null }];
      this.debitFields = [{ glAccountId: null, amount: null }];
      this.paymentType = "";
      this.account = "";
      this.bank = "";
      this.cheque = "";
      this.receipt = "";
      this.routingCode = "";
    },
  },

  mounted() {
    this.getAllowedAccountsMethod();
    this.getOfficeList();
    this.getCurrencyDataMethod();
    this.getPaymentTypesMethod();
  },
};
</script>
<style>
.icon-add {
  margin-left: 4rem !important;
  margin-top: 1.5rem !important;
}
.custom-width {
  width: 47.5% !important;
}
.custom-margin {
  margin-left: 2rem !important;
}
</style>
